import React, { useEffect, useState } from "react";
import { CreditCard, ArrowUpRight, AlertTriangle, Check, X, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { toast } from "sonner";

const TIERS = [
  { key: "Tier1", label: "Starter", price: "€49/yr", features: ["10 active clients", "Unlimited contracts", "Brand Hub", "Magic links"] },
  { key: "Tier2", label: "Growth", price: "€129/yr", features: ["30 active clients", "Custom domain", "Outbound webhooks", "Priority support"] },
  { key: "Tier3", label: "Scale", price: "€229/yr", features: ["60 active clients", "White-label portal", "Team seats (soon)", "Dedicated Slack"] },
  { key: "Tier4", label: "Agency", price: "€399/yr", features: ["Unlimited clients", "Migration help", "Priority onboarding", "Custom integrations"] },
];

const PENDING_TYPE = { cancel_requested: "cancel", upgrade_requested: "upgrade" };

export default function Subscription() {
  const [sub, setSub] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    const { data } = await api.get("/agency/subscription");
    setSub(data);
  };

  useEffect(() => { load(); }, []);

  const requestCancel = async () => {
    try {
      await api.post("/agency/subscription/cancel", { reason });
      toast.success("Cancellation requested. Our team will reach out shortly.");
      setShowCancel(false);
      setReason("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const requestUpgrade = async (tier) => {
    try {
      await api.post("/agency/subscription/upgrade", { target_tier: tier });
      toast.success(`Upgrade to ${tier} requested. Our team will reach out with payment.`);
      setShowUpgrade(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const revert = async () => {
    try {
      await api.post("/agency/subscription/revert");
      toast.success("Request withdrawn");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!sub) return <div className="p-12 text-slate-400">Loading…</div>;

  const current = TIERS.find((t) => t.key === sub.appsumo_tier) || TIERS[0];
  const pending = PENDING_TYPE[sub.subscription_status] || null;

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <p className="eyebrow mb-2">Your plan</p>
      <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-8" data-testid="subscription-title">Subscription</h1>

      {/* Current plan card */}
      <div className="bento p-8 mb-6 grain-overlay bg-slate-900 text-white" data-testid="current-plan-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="eyebrow text-slate-400 mb-2">Current plan</p>
            <h2 className="text-4xl font-bold tracking-tight mb-1">{current.label} · {sub.appsumo_tier}</h2>
            <p className="text-slate-400 text-sm">
              Status: <span className={`font-mono ${sub.subscription_status === "active" ? "text-emerald-400" : "text-amber-400"}`}>{sub.subscription_status}</span>
              {sub.is_suspended && <span className="ml-2 text-red-400">· suspended</span>}
            </p>
          </div>
          <CreditCard className="w-8 h-8 text-slate-400" />
        </div>
      </div>

      {/* Pending request banner */}
      {pending && (
        <div className="bento p-6 mb-6 bg-amber-50 border-amber-200 flex items-start gap-4" data-testid="pending-request-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-amber-900">
            <p className="font-semibold mb-1">
              {pending === "cancel" ? "Cancellation requested" : `Upgrade to ${sub.requested_tier} requested`}
            </p>
            <p>Our team will reach out via email to finalize. Changed your mind?</p>
          </div>
          <button onClick={revert} className="text-sm bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-full px-4 py-2 inline-flex items-center gap-1 font-medium" data-testid="revert-request-button">
            <Undo2 className="w-3.5 h-3.5" /> Withdraw
          </button>
        </div>
      )}

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8" data-testid="tier-cards">
        {TIERS.map((t) => {
          const isCurrent = t.key === sub.appsumo_tier;
          return (
            <div key={t.key} className={`bento p-8 ${isCurrent ? "border-slate-900 border-2" : ""}`} data-testid={`tier-card-${t.key}`}>
              {isCurrent && <span className="text-xs bg-slate-900 text-white px-2 py-1 rounded-full font-medium mb-3 inline-block">Current</span>}
              <h3 className="text-2xl font-bold tracking-tight mb-1">{t.label}</h3>
              <p className="text-4xl font-bold mb-4">{t.price}</p>
              <ul className="space-y-2 mb-6">
                {t.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              {!isCurrent && !pending && (
                <button onClick={() => setShowUpgrade(t.key)} className="w-full bg-slate-900 text-white rounded-full py-2.5 font-medium hover:bg-slate-700 active:scale-95 inline-flex items-center justify-center gap-1" data-testid={`upgrade-to-${t.key}`}>
                  {TIERS.findIndex(x => x.key === t.key) > TIERS.findIndex(x => x.key === sub.appsumo_tier) ? "Upgrade" : "Switch"} <ArrowUpRight className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Cancel section */}
      {!pending && (
        <div className="bento p-6 bg-slate-50" data-testid="cancel-section">
          <p className="eyebrow mb-2">Danger zone</p>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-slate-700">Need to cancel? We'll keep your data for 30 days before permanent deletion.</p>
            <button onClick={() => setShowCancel(true)} className="text-red-600 hover:bg-red-50 border border-red-200 rounded-full px-5 py-2 font-medium inline-flex items-center gap-1 text-sm" data-testid="request-cancel-button">
              <X className="w-4 h-4" /> Cancel subscription
            </button>
          </div>
        </div>
      )}

      {/* Cancel modal */}
      {showCancel && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCancel(false)}>
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="cancel-modal">
            <h2 className="text-2xl font-bold mb-3">Cancel subscription?</h2>
            <p className="text-sm text-slate-600 mb-5">We're sorry to see you go. Your request will be reviewed by our team and processed within 48 hours.</p>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Reason (optional)</label>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Help us improve…" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-5" data-testid="cancel-reason-input" />
            <div className="flex gap-3">
              <button onClick={() => setShowCancel(false)} className="flex-1 bg-slate-100 hover:bg-slate-200 rounded-full py-2.5 font-medium" data-testid="cancel-modal-close">Keep plan</button>
              <button onClick={requestCancel} className="flex-1 bg-red-600 text-white hover:bg-red-700 rounded-full py-2.5 font-medium" data-testid="confirm-cancel-button">Confirm cancellation</button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade modal */}
      {showUpgrade && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowUpgrade(null)}>
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="upgrade-modal">
            <h2 className="text-2xl font-bold mb-2">Switch to {TIERS.find((t) => t.key === showUpgrade)?.label}?</h2>
            <p className="text-sm text-slate-600 mb-5">We'll reach out via email to collect payment and apply the change. Your current plan stays active until then.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowUpgrade(null)} className="flex-1 bg-slate-100 hover:bg-slate-200 rounded-full py-2.5 font-medium">Back</button>
              <button onClick={() => requestUpgrade(showUpgrade)} className="flex-1 bg-slate-900 text-white hover:bg-slate-700 rounded-full py-2.5 font-medium" data-testid="confirm-upgrade-button">Request switch</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
